import React, { useEffect, useState } from "react";
import { MonitorGridStrings } from "./types";

interface PaginationProps {
  page: number;
  rp: number;
  total: number;
  rowCount: number;
  gridStrings?: MonitorGridStrings;
  loading: boolean;
  searchVisible: boolean;
  onPageChange: (nextPage: number) => void;
  onPageSizeChange: (nextPageSize: number) => void;
  onRefresh: () => void;
  onToggleSearch: () => void;
}

function formatPagestat(template: string, fromValue: number, toValue: number, totalValue: number): string {
  return template
    .replace(/\{from\}/g, String(fromValue))
    .replace(/\{to\}/g, String(toValue))
    .replace(/\{total\}/g, String(totalValue));
}

export default function Pagination(props: PaginationProps) {
  const {
    page,
    rp,
    total,
    rowCount,
    gridStrings,
    loading,
    searchVisible,
    onPageChange,
    onPageSizeChange,
    onRefresh,
    onToggleSearch
  } = props;
  const totalPages = Math.max(1, Math.ceil(total / rp));
  const clampedPage = Math.min(Math.max(page, 1), totalPages);
  const firstRow = total === 0 ? 0 : (clampedPage - 1) * rp + 1;
  const lastRow = total === 0 ? 0 : Math.min(total, firstRow + Math.max(rowCount - 1, 0));
  const [pageInput, setPageInput] = useState<string>(String(clampedPage));

  useEffect(() => {
    setPageInput(String(clampedPage));
  }, [clampedPage]);

  const jumpToPage = () => {
    const parsed = parseInt(pageInput, 10);
    if (isNaN(parsed)) {
      setPageInput(String(clampedPage));
      return;
    }
    onPageChange(Math.min(Math.max(parsed, 1), totalPages));
  };

  const strings = gridStrings || ({} as MonitorGridStrings);
  const pageLabel = strings.page_label || "Page";
  const pageOfLabel = strings.page_of_label || "of";

  const pagestat = (() => {
    if (loading && total === 0) {
      return strings.procmsg || "Processing, please wait...";
    }
    if (total === 0) {
      return strings.nomsg || "No Patients to display";
    }
    if (total === 1) {
      return strings.pagestat_one || "Displaying 1 Patient";
    }
    return formatPagestat(
      strings.pagestat_many || "Displaying {from} to {to} of {total} Patients",
      firstRow,
      lastRow,
      total
    );
  })();

  const prevDisabled = clampedPage <= 1;
  const nextDisabled = clampedPage >= totalPages;

  return (
    <div className="pDiv">
      <div className="pDiv2">
        <div className="pGroup pSearch">
          <div
            className={`pButton ${searchVisible ? "pSearchActive" : ""}`}
            onClick={onToggleSearch}
          >
            <span><i className="fas fa-search ml-2" /></span>
          </div>
        </div>
        <div className="pGroup">
          <select
            className="rpSelect"
            value={rp}
            onChange={(event) => onPageSizeChange(parseInt(event.target.value, 10))}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
        <div className="pGroup">
          <div
            className={`pButton pFirst ${prevDisabled ? "pBtnDisable" : ""}`}
            onClick={() => !prevDisabled && onPageChange(1)}
          >
            <span><i className="fas fa-step-backward" /></span>
          </div>
          <div
            className={`pButton pPrev ${prevDisabled ? "pBtnDisable" : ""}`}
            onClick={() => !prevDisabled && onPageChange(clampedPage - 1)}
          >
            <span><i className="fas fa-chevron-left" /></span>
          </div>
        </div>
        <div className="pGroup">
          {pageLabel}
          <input
            type="text"
            size={4}
            className="pcontrol"
            value={pageInput}
            onChange={(event) => setPageInput(event.target.value)}
            onBlur={jumpToPage}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                jumpToPage();
              }
            }}
          />
          {` ${pageOfLabel} ${totalPages} `}
        </div>
        <div className="pGroup">
          <div
            className={`pButton pNext ${nextDisabled ? "pBtnDisable" : ""}`}
            onClick={() => !nextDisabled && onPageChange(clampedPage + 1)}
          >
            <span><i className="fas fa-chevron-right" /></span>
          </div>
          <div
            className={`pButton pLast ${nextDisabled ? "pBtnDisable" : ""}`}
            onClick={() => !nextDisabled && onPageChange(totalPages)}
          >
            <span><i className="fas fa-step-forward" /></span>
          </div>
        </div>
        <div className="pGroup">
          <div className="pButton pReload" onClick={onRefresh}>
            <span><i className="fas fa-sync" /></span>
          </div>
        </div>
        <div className="pGroup">
          <span className="pPageStat">{pagestat}</span>
        </div>
      </div>
    </div>
  );
}
